import { useEffect, useState } from 'react';

import { resetHeat, startHeat, stopHeat } from '../lib/api.js';

/**
 * El reloj del heat, y los mandos para arrancarlo.
 *
 * Son dos cosas distintas y antes estaban pegadas: el componente entero
 * hacia `return null` cuando el WOD no tenia time cap, asi que sin cap no
 * habia boton de arrancar en ninguna parte de la aplicacion --y `start_heat`
 * no se llama desde ningun otro sitio. Resultado: un WOD sin tope no podia
 * empezar nunca y su proyeccion se quedaba en "EN BREVE" para siempre.
 *
 * Ahora el heat siempre se puede arrancar. El reloj cuenta ATRAS si hay
 * tope --que es lo que el juez necesita para cantar el corte-- y hacia
 * DELANTE si no lo hay, que es lo util en una ventana de intentos.
 *
 * El instante de arranque viene del servidor. Aqui solo se pinta la
 * diferencia, corregida por el desfase del reloj de esta tablet: una con
 * el reloj adelantado dos minutos enseñaria el cronometro adelantado dos
 * minutos, y dos jueces del mismo heat cantarian el cap con dos minutos
 * de diferencia.
 */
export function HeatTimer({
  startedAt,
  capSeconds,
  offsetMs,
  heatId,
  canControl,
  showClock,
  onChange,
}: {
  startedAt: string | null;
  capSeconds: number | null;
  /** El organizador puede apagar el reloj y dejar solo los mandos. */
  showClock: boolean;
  /** Reloj del servidor menos reloj de esta tablet, en milisegundos. */
  offsetMs: number;
  heatId: string;
  canControl: boolean;
  onChange: () => void;
}) {
  const [ahora, setAhora] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Un tick cada 200 ms, no cada segundo: con un segundo justo, el número
  // salta con hasta un segundo de retraso y en el último tramo se nota.
  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => setAhora(Date.now()), 200);
    return () => clearInterval(id);
  }, [startedAt]);

  // Sin reloj y sin mandos no hay nada que enseñar.
  if (!showClock && !canControl) return null;

  const corriendo = Boolean(startedAt);
  const transcurrido = startedAt
    ? Math.max(0, (ahora + offsetMs - new Date(startedAt).getTime()) / 1000)
    : 0;
  const restante = capSeconds ? Math.max(0, capSeconds - transcurrido) : null;
  const agotado = restante !== null && corriendo && restante <= 0;
  // Último minuto: el juez tiene que estar mirando al atleta, no al reloj.
  const ultimo = restante !== null && corriendo && !agotado && restante <= 60;

  async function accion(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChange();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`timer ${agotado ? 'is-capped' : ''} ${ultimo ? 'is-last' : ''}`}>
      {showClock && (
        <div className="timer-clock" aria-live="off">
          {agotado
            ? 'TIME CAP'
            : formatClock(restante !== null ? (corriendo ? restante : capSeconds!) : transcurrido)}
        </div>
      )}

      <div className="timer-meta">
        {!showClock ? (
          <span>{corriendo ? 'heat en curso' : 'heat sin empezar'}</span>
        ) : capSeconds ? (
          <span>{corriendo ? `de ${formatClock(capSeconds)}` : `time cap ${formatClock(capSeconds)}`}</span>
        ) : (
          // Sin tope el número de arriba es tiempo transcurrido, y hay que
          // decirlo: un juez que lo lea como cuenta atrás canta un corte
          // que no existe.
          <span>{corriendo ? 'transcurrido · sin tope' : 'sin tope'}</span>
        )}
      </div>

      {canControl && (
        <div className="timer-controls">
          {!corriendo ? (
            <button className="primary" disabled={busy} onClick={() => accion(() => startHeat(heatId))}>
              Empezar heat
            </button>
          ) : (
            <>
              <button disabled={busy} onClick={() => accion(() => stopHeat(heatId))}>
                Cerrar heat
              </button>
              {/* Para una salida nula: deja el heat como si no hubiera
                  empezado, en vez de arrastrar un tiempo que no vale. */}
              <button className="ghost" disabled={busy} onClick={() => accion(() => resetHeat(heatId))}>
                Reiniciar
              </button>
            </>
          )}
        </div>
      )}

      {error && <p className="timer-error">{error}</p>}
    </div>
  );
}

/** mm:ss, y h:mm:ss solo si de verdad pasa de la hora. */
function formatClock(seconds: number): string {
  const total = Math.ceil(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const dos = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${dos(m)}:${dos(s)}` : `${m}:${dos(s)}`;
}
